import axios from "axios";
import { useState, useEffect, useCallback } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

export const TOKEN_KEY = "sx_token";
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const apiGet = async (p) => (await api.get(p)).data;
export const apiPost = async (p, b) => (await api.post(p, b)).data;
export const apiPut = async (p, b) => (await api.put(p, b)).data;
export const apiDelete = async (p) => (await api.delete(p)).data;

export async function downloadPdf(path, filename, body) {
  const res = body ? await api.post(path, body, { responseType: "blob" }) : await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

export function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet(path);
      setData(d);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, deps);
  return { data, loading, error, reload, setData };
}

export function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const STATUS_BADGE = {
  ACTIVE: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
  PROTECTED: "bg-blue-500/10 text-blue-400 border border-blue-500/30",
  MONITORING: "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30",
  AT_RISK: "bg-orange-500/10 text-orange-400 border border-orange-500/30",
  ISOLATED: "bg-rose-500/15 text-rose-300 border border-rose-500/40",
  BLOCKED: "bg-red-600/20 text-red-300 border border-red-600/40",
  RECOVERY: "bg-indigo-500/10 text-indigo-300 border border-indigo-500/30",
  DISABLED: "bg-slate-800/60 text-slate-400 border border-slate-700/50",
  CONNECTED: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
  PENDING: "bg-amber-500/10 text-amber-300 border border-amber-500/30",
  UNTRUSTED: "bg-rose-500/15 text-rose-300 border border-rose-500/40",
  DENIED: "bg-slate-800/60 text-slate-400 border border-slate-700/50",
  ALLOW: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  DENY: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  REVIEW: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  TEMPORARY: "bg-purple-500/15 text-purple-300 border border-purple-500/30",
  REVOKE: "bg-red-500/20 text-red-400 border border-red-500/50",
  // incident states
  NEW: "bg-blue-500/10 text-blue-300 border border-blue-500/30",
  INVESTIGATING: "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30",
  CONTAINED: "bg-indigo-500/10 text-indigo-300 border border-indigo-500/30",
  RECOVERING: "bg-indigo-500/10 text-indigo-300 border border-indigo-500/30",
  RESOLVED: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
  FALSE_POSITIVE: "bg-slate-800/60 text-slate-400 border border-slate-700/50",
  DETECTED: "bg-orange-500/10 text-orange-400 border border-orange-500/30",
  MITIGATED: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
  // protection onboarding states
  NOT_CONNECTED: "bg-slate-800/60 text-slate-400 border border-slate-700/50",
  VERIFICATION_REQUIRED: "bg-amber-500/10 text-amber-300 border border-amber-500/30",
  CONFIGURATION_REQUIRED: "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30",
  ACTION_BLOCKED: "bg-red-600/20 text-red-300 border border-red-600/40",
  // authorization request statuses
  APPROVED: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
  APPROVED_ONCE: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30",
  DENIED: "bg-rose-500/15 text-rose-300 border border-rose-500/40",
  REJECTED: "bg-red-600/20 text-red-300 border border-red-600/40",
  VERIFICATION_REQUESTED: "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30",
  REVOKED: "bg-red-500/20 text-red-400 border border-red-500/50",
  // org verification states
  UNVERIFIED: "bg-slate-800/60 text-slate-400 border border-slate-700/50",
  VERIFICATION_IN_PROGRESS: "bg-amber-500/10 text-amber-300 border border-amber-500/30",
  VERIFIED: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
  VERIFICATION_FAILED: "bg-rose-500/15 text-rose-300 border border-rose-500/40",
  PROTECTION_PENDING: "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30",
};

export const RISK_BADGE = {
  LOW: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
  MEDIUM: "bg-amber-500/10 text-amber-300 border border-amber-500/30",
  HIGH: "bg-orange-500/10 text-orange-400 border border-orange-500/30",
  CRITICAL: "bg-red-500/15 text-red-400 border border-red-500/40",
};

export function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

export function timeAgo(s) {
  if (!s) return "—";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const CHART_COLORS = ["#2563EB", "#06B6D4", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"];
